import type { SiteCmsDraftResponse, SiteCmsPublicationSummary } from '@shared/types/entities';

import { cmsSameOwner, cmsContentKey as content } from './site-cms-interaction-model';
import { cmsAdoptDraftIdentities } from './site-cms-workspace-model';

export type CmsSection = 'overview' | 'pages' | 'design' | 'navigation' | 'documents' | 'publication' | 'settings';
export type CmsSession = { draft: SiteCmsDraftResponse; saved: SiteCmsDraftResponse };
export type CmsSessionAction =
  | { type: 'edit'; draft: SiteCmsDraftResponse }
  | { type: 'reload'; draft: SiteCmsDraftResponse }
  | { type: 'rebase'; server: SiteCmsDraftResponse }
  | { type: 'saved'; response: SiteCmsDraftResponse; submitted: SiteCmsDraftResponse };

export function isCmsDirty(session: CmsSession): boolean {
  return content(session.draft) !== content(session.saved);
}
export function cmsSessionReducer(state: CmsSession, action: CmsSessionAction): CmsSession {
  if (action.type === 'edit')
    return cmsSameOwner(state.draft, action.draft)
      ? { ...state, draft: { ...action.draft, revision: state.draft.revision, updatedAt: state.draft.updatedAt } }
      : state;
  if (action.type === 'reload')
    return cmsSameOwner(state.draft, action.draft) ? { draft: action.draft, saved: action.draft } : state;
  if (action.type === 'rebase') {
    if (action.server.ownerType !== state.draft.ownerType || action.server.ownerId !== state.draft.ownerId)
      return state;
    return {
      saved: action.server,
      draft: { ...state.draft, revision: action.server.revision, updatedAt: action.server.updatedAt },
    };
  }
  if (action.response.ownerType !== state.draft.ownerType || action.response.ownerId !== state.draft.ownerId)
    return state;
  if (!cmsSameOwner(action.submitted, state.draft) || action.response.revision < state.saved.revision) return state;
  // Server ACK must not overwrite typing that happened while the request was in flight.
  const draft =
    content(state.draft) === content(action.submitted)
      ? cmsAdoptDraftIdentities(state.draft, action.response)
      : {
          ...state.draft,
          revision: action.response.revision,
          updatedAt: action.response.updatedAt,
        };
  return { saved: action.response, draft };
}

/** "latest" describes the last preparation, not necessarily the still-live release. */
export function cmsPublicationLabel(latest: SiteCmsPublicationSummary | null | undefined, failed = false): string {
  if (failed) return 'Statut indisponible';
  if (latest === undefined) return 'Vérification du statut…';
  if (latest === null) return 'Aucune version préparée';
  const labels: Record<SiteCmsPublicationSummary['status'], string> = {
    PREPARED: 'préparée',
    STAGED: 'prête à activer',
    LIVE: 'en ligne',
    FAILED: 'en échec',
    REVOKED: 'retirée',
  };
  return `Révision ${latest.revision} ${labels[latest.status]}`;
}
